import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Droplets, Sun, Sprout, CheckCircle2 } from "lucide-react";

const features = [
  { icon: Droplets, title: "Drip & Sprinkler Systems", desc: "45%–55% subsidy on approved micro-irrigation systems that deliver water directly to crop roots." },
  { icon: Sun, title: "Solar Pump Integration", desc: "Guidance on combining PMKSY with solar pump schemes for lower running costs." },
  { icon: Sprout, title: "Per Drop More Crop", desc: "Improve water-use efficiency, reduce wastage and boost yield with precision irrigation." },
];

export default function Pmksy() {
  const navigate = useNavigate();
  return (
    <MarketingLayout>
      <section className="relative grain" style={{ background: "radial-gradient(1000px 400px at 80% -10%, rgba(31,107,59,0.12), transparent 60%), #eef6f0" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="text-sm text-slate-500 mb-4"><Link to="/" className="hover:text-emerald-700">Home</Link> / PMKSY Irrigation</div>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl lg:text-5xl text-emerald-950 tracking-tight max-w-3xl">PMKSY Micro-Irrigation Assistance</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">Pradhan Mantri Krishi Sinchayee Yojana (Per Drop More Crop) supports drip & sprinkler irrigation with 45%–55% subsidy. We help you assess, document and apply.</p>
          <Button className="mt-7 rounded-full bg-brand hover:bg-emerald-800 text-white h-12 px-7" onClick={() => navigate("/login")} data-testid="pmksy-start-button">Request Assistance <ArrowRight className="ml-2 h-5 w-5" /></Button>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((f) => (
              <Card key={f.title} className="rounded-3xl p-7 border-emerald-100">
                <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center"><f.icon className="h-7 w-7 text-emerald-700" /></div>
                <h3 className="mt-5 font-head font-bold text-xl text-emerald-950">{f.title}</h3>
                <p className="mt-2.5 text-slate-500 text-[15px] leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>

          <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="font-head font-bold text-2xl sm:text-3xl text-emerald-950">How we support your PMKSY application</h2>
              <ul className="mt-6 space-y-3">
                {["Water source & crop assessment for the right system", "Bill of Quantity (BoQ) & vendor quotation help", "Land and Aadhaar document organisation", "Application preparation and submission to the department", "Status tracking and follow-up support"].map((t) => (
                  <li key={t} className="flex gap-3 text-slate-600"><CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" /><span className="text-sm">{t}</span></li>
                ))}
              </ul>
            </div>
            <Card className="rounded-3xl overflow-hidden border-emerald-100">
              <img src="https://images.unsplash.com/photo-1692369584496-3216a88f94c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Drip irrigation" className="w-full h-64 object-cover" />
              <div className="p-6">
                <h4 className="font-head font-semibold text-emerald-950">Save water, grow more</h4>
                <p className="mt-2 text-sm text-slate-500">Micro-irrigation can cut water use by 40–60% while improving yields — a smart, subsidised upgrade for your farm.</p>
                <Button className="mt-4 rounded-full bg-brand hover:bg-emerald-800 text-white" onClick={() => navigate("/login")}>Start Now <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
